import { useEffect, useState, useCallback } from "react";
import { db, auth } from "../services/firebase";
import { ref, set, get, onValue } from "firebase/database";

/**
 * Handles test completion status tracking:
 * ✅ Save finished status
 * ✅ Fetch current status (finished or not)
 * ✅ Listen for status updates in real-time
 */
export default function useTestStatus(domainId, packId, interviewId) {
    const [status, setStatus] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(true);

    // ✅ Realtime listener to detect status changes
    useEffect(() => {
        const user = auth.currentUser;
        if (!user || !domainId || !packId || !interviewId) return;

        const statusRef = ref(db, `Tests/Status/${user.uid}/${domainId}/${packId}/${interviewId}`);
        const unsubscribe = onValue(statusRef, (snapshot) => {
            setStatus(snapshot.val());
            setLoadingStatus(false);
        });

        return () => unsubscribe();
    }, [domainId, packId, interviewId]);

    // ✅ Manually fetch once (optional)
    const fetchStatusOnce = useCallback(async () => {
        const user = auth.currentUser;
        if (!user) return null;
        const statusRef = ref(db, `Tests/Status/${user.uid}/${domainId}/${packId}/${interviewId}`);
        const snap = await get(statusRef);
        return snap.exists() ? snap.val() : null;
    }, [domainId, packId, interviewId]);

    // ✅ Mark test as finished
    const markAsFinished = useCallback(
        async (totalQuestions) => {
            const user = auth.currentUser;
            if (!user) return;

            const statusRef = ref(db, `Tests/Status/${user.uid}/${domainId}/${packId}/${interviewId}`);
            const payload = {
                status: "finished",
                finishedAt: Date.now(),
                totalQuestions: totalQuestions || 0,
            };

            await set(statusRef, payload);
            setStatus(payload);
        },
        [domainId, packId, interviewId]
    );

    return {
        status, // current test status (object or null)
        loadingStatus, // boolean
        markAsFinished,
        fetchStatusOnce,
    };
}
